import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Select, Spin, Descriptions, Alert, App } from 'antd';
import { useQuery, useMutation } from '@tanstack/react-query';
import { userService } from '../../../services/cms/userServiceRequest';
import useDebounce from '../../../hooks/useDebounce';

/**
 * Gộp tài khoản ("Nhập tài khoản" của CMS cũ): chuyển toàn bộ dữ liệu của
 * "Tài khoản Xóa" sang "Tài khoản Giữ", rồi khoá tài khoản bị gộp và đánh dấu
 * ĐÃ GỘP (status = 3) — không xoá dòng, giữ lại để đối soát.
 *
 * Logic gộp chạy trong lv-cms-v4-api (userService.mergeUserService, transaction
 * trên DB lichviet) — port nguyên bản lichviet_cms, không gọi core.
 */

// Giữ đúng bảng ánh xạ đang dùng ở màn chi tiết tài khoản (userDetail.js)
const SIGNAL_TYPE_NAME = {
  1: 'Email',
  2: 'Facebook',
  3: 'Số điện thoại',
  4: 'Apple',
  5: 'Zalo',
};

const userLabel = (u) => {
  const name = u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || '(chưa có tên)';
  const contact = u.email || u.phone || '';
  return contact ? `#${u.id} — ${name} (${contact})` : `#${u.id} — ${name}`;
};

/** Ô tìm kiếm tài khoản: gõ được ID, email, SĐT hoặc tên (API tự đoán loại keyword). */
const UserPicker = ({ value, onChange, placeholder }) => {
  const [keyword, setKeyword] = useState('');
  // Giữ lại option đã chọn: mỗi lần gõ tìm mới, kết quả cũ biến mất khỏi
  // `options` và antd sẽ hiện ID trần thay vì nhãn.
  const [selectedOption, setSelectedOption] = useState(null);
  const debouncedKeyword = useDebounce(keyword, 500);

  const { data, isFetching } = useQuery({
    queryKey: ['merge-user-search', debouncedKeyword],
    queryFn: () => userService.getList({ keyword: debouncedKeyword, limit: 20, page: 1 }),
    enabled: !!debouncedKeyword,
  });

  const options = useMemo(() => {
    const list = Array.isArray(data?.data) ? data.data : [];
    const found = list.map((u) => ({ label: userLabel(u), value: u.id }));
    if (selectedOption && !found.some((o) => o.value === selectedOption.value)) {
      return [selectedOption, ...found];
    }
    return found;
  }, [data, selectedOption]);

  const handleChange = (val, option) => {
    setSelectedOption(val === undefined || val === null ? null : option);
    onChange(val);
  };

  return (
    <Select
      showSearch
      allowClear
      className="w-full"
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      onSearch={setKeyword}
      filterOption={false}
      options={options}
      notFoundContent={isFetching ? <Spin size="small" /> : (debouncedKeyword ? 'Không tìm thấy' : 'Nhập ID, email, SĐT hoặc tên')}
    />
  );
};

UserPicker.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

/** Panel đối chiếu — đúng 5 mục như CMS cũ. */
const UserInfoPanel = ({ id }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['merge-user-info', id],
    queryFn: () => userService.getOne({ id }),
    enabled: !!id,
  });

  if (!id) return null;
  if (isLoading) return <div className="py-6 text-center"><Spin /></div>;

  const user = data?.user || {};
  const signals = data?.signals || [];
  const premiums = data?.premiums || [];

  const premiumText = premiums.length
    ? premiums.map((p) => `${p.type_name || `Nhóm ${p.premium_groups}`} — hết hạn ${p.end_time || '-'}`).join('; ')
    : 'Không có';

  const signalText = signals.length
    ? [...new Set(signals.map((s) => SIGNAL_TYPE_NAME[s.type] || `Khác (${s.type})`))].join(', ')
    : 'Không có';

  const items = [
    { key: 'phone', label: 'Số điện thoại', children: user.phone || '-' },
    { key: 'email', label: 'Email', children: user.email || '-' },
    { key: 'event', label: 'Tổng sự kiện', children: data?.total_event ?? 0 },
    { key: 'premium', label: 'Thông tin gói mua lịch việt', children: premiumText },
    { key: 'signal', label: 'Các hình thức đăng nhập đã sử dụng', children: signalText },
  ];

  return <Descriptions column={1} size="small" bordered items={items} labelStyle={{ width: 190 }} />;
};

UserInfoPanel.propTypes = {
  id: PropTypes.number,
};

const MergeUserModal = ({ open, onClose, onSuccess }) => {
  const { message, modal } = App.useApp();
  const [userKeep, setUserKeep] = useState(undefined);
  const [userMerge, setUserMerge] = useState(undefined);

  const reset = () => {
    setUserKeep(undefined);
    setUserMerge(undefined);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const mergeMutation = useMutation({
    mutationFn: () => userService.merge({ user_keep: userKeep, user_merge: userMerge }),
    onSuccess: (res) => {
      if (res?.code && res.code !== 200) {
        message.error(res.message || 'Gộp tài khoản thất bại!');
        return;
      }
      message.success('Gộp tài khoản thành công!');
      reset();
      onClose();
      if (onSuccess) onSuccess();
    },
    onError: (error) => {
      // Interceptor axios đã tự hiện thông báo cho lỗi 400; chỉ fallback phần còn lại
      if (error?.status === 400) return;
      if (error?.status === 403) {
        message.error('Bạn không có quyền gộp tài khoản.');
        return;
      }
      message.error(error?.data?.message || error?.message || 'Gộp tài khoản thất bại!');
    },
  });

  const handleSubmit = () => {
    if (!userKeep || !userMerge) {
      message.error('Vui lòng chọn cả tài khoản giữ và tài khoản xoá!');
      return;
    }
    // mergeAccount của core im lặng bỏ qua khi 2 ID trùng nhau. Đã chặn ở cả API
    // lẫn core, chặn thêm ở đây để khỏi mất một vòng request.
    if (userKeep === userMerge) {
      message.error('Tài khoản giữ và tài khoản xoá không được trùng nhau!');
      return;
    }

    modal.confirm({
      title: 'Xác nhận gộp tài khoản',
      okText: 'Gộp',
      okButtonProps: { danger: true },
      cancelText: 'Bỏ qua',
      width: 520,
      content: (
        <div>
          Sẽ chuyển toàn bộ dữ liệu (sự kiện, nhắc nhở, gói premium, lịch sử giao dịch, phương thức
          đăng nhập...) từ tài khoản <strong>#{userMerge}</strong> sang tài khoản{' '}
          <strong>#{userKeep}</strong>, rồi <strong>khoá tài khoản #{userMerge}</strong> và đánh dấu
          ĐÃ GỘP. SĐT/email của #{userMerge} sẽ thay thế trên #{userKeep} nếu có.
          <div className="mt-2 text-red-600 font-medium">Thao tác này không thể hoàn tác.</div>
        </div>
      ),
      onOk: async () => {
        try {
          await mergeMutation.mutateAsync();
        } catch (e) {
          // onError đã hiện thông báo — nuốt lỗi để hộp xác nhận đóng lại
        }
      },
    });
  };

  return (
    <Modal
      title="Nhập tài khoản"
      open={open}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText="Nhập"
      cancelText="Bỏ qua"
      okButtonProps={{ danger: true, loading: mergeMutation.isPending }}
      width={900}
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        className="mb-4"
        message="Sau khi gộp, Tài khoản Xóa sẽ bị khoá và đánh dấu ĐÃ GỘP; toàn bộ dữ liệu chuyển sang Tài khoản Giữ. Không thể hoàn tác."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          {/* Modal này không dùng Form nên không có dấu * của antd — đánh dấu thủ công để
              hai ô chọn tài khoản (đều bắt buộc, chặn ở handleSubmit) nhìn ra được (QA-13). */}
          <div className="font-semibold mb-2">
            <span className="text-red-500 mr-1">*</span>Tài khoản Giữ
          </div>
          <UserPicker
            value={userKeep}
            onChange={setUserKeep}
            placeholder="Tìm kiếm ..."
          />
          <div className="mt-3">
            <UserInfoPanel id={userKeep} />
          </div>
        </div>

        <div>
          <div className="font-semibold mb-2">
            <span className="text-red-500 mr-1">*</span>Tài khoản Xóa
          </div>
          <UserPicker
            value={userMerge}
            onChange={setUserMerge}
            placeholder="Tìm kiếm ..."
          />
          <div className="mt-3">
            <UserInfoPanel id={userMerge} />
          </div>
        </div>
      </div>
    </Modal>
  );
};

MergeUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default MergeUserModal;
